import React from "react";
import Navbar from "../navbar/navbar-desktop.tsx";
import Footer from "../footer/footer.tsx";
import { Link } from "react-router-dom";
import { useJsonLd } from "../seo/seo-manager.tsx";
import { FaHotTub, FaLeaf } from "react-icons/fa";
import { GiWaterDrop, GiPineTree, GiHotSurface } from "react-icons/gi";
import { MdOutlineSpa } from "react-icons/md";

const SITE_URL = "https://www.hospudkapodrablinu.cz";

/* ============================================================
   DATA – přednosti wellness (ikony + krátké benefity)
============================================================ */
const PREDNOSTI = [
  {
    icon: <FaHotTub />,
    title: "2 vířivky",
    text: "Venkovní pod hvězdami a velká krytá až pro 5 osob.",
  },
  {
    icon: <GiHotSurface />,
    title: "Finská sauna na dřevo",
    text: "Pravé sálavé teplo ze saunových kamen na dřevo, ne z elektriky.",
  },
  {
    icon: <GiPineTree />,
    title: "Výhled do valašských lesů",
    text: "Lesy, květinové louky a naprostý klid Hostýnských vrchů.",
  },
];

/* ============================================================
   DATA – FAQ (zároveň se propisuje do FAQPage JSON-LD)
============================================================ */
const FAQ = [
  {
    q: "Kolik lidí se do wellness vejde?",
    a: "Do kryté vířivky se pohodlně vejde až 5 osob, do venkovní vířivky menší skupinka.",
  },
  {
    q: "Dá se nastavit teplota vody ve vířivce?",
    a: "Ano. Teplotu vody vám po domluvě s majitelem nastavíme přesně podle vašeho přání – ať už chcete horkou lázeň v zimě, nebo svěžejší vodu v létě.",
  },
  {
    q: "Je sauna vytápěná dřevem?",
    a: "Ano, finská sauna je vytápěná saunovými kamny na dřevo. Sálavé teplo z kamen na dřevo je jemnější, voňavější a příjemnější než u elektrických saun.",
  },
  {
    q: "Jak si wellness zarezervovat?",
    a: "Nejrychleji telefonicky na čísle +420 608 608 101, nebo přes online formulář rezervace wellness, kde vyplníte termín, počet osob a případný zájem o ubytování.",
  },
];

/* ============================================================
   DATA – galerie
   Všechny fotky jsou reálné z img/wellness.
============================================================ */
const GALERIE = [
  { src: "./img/wellness/wellness-virivka-venkovni.jpg", alt: "Venkovní vířivka a lehátka na kamenné podlaze" },
  { src: "./img/wellness/wellness-sauna-finska.jpg", alt: "Prosklená finská sauna vytápěná dřevem ve večerním nasvícení" },
  { src: "./img/wellness/wellness-vyhled-lesy-louky.jpg", alt: "Výhled z wellness do valašských lesů a luk za soumraku" },
  { src: "./img/wellness/wellness-virivka-kryta.jpg", alt: "Krytá vířivka v dřevěném obložení s nasvícením" },
  { src: "./img/wellness/wellness-odpocivarna.jpg", alt: "Prosklená odpočívárna a lehátka na kamenné terase" },
  { src: "./img/wellness/wellness-venkovni-zahradka.jpg", alt: "Venkovní zahrádka wellness s kameny, bonsajemi a posezením" },
];

const Wellness: React.FC = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    name: "Wellness – Hospůdka pod Rablinů",
    url: `${SITE_URL}/wellness`,
    image: `${SITE_URL}/img/wellness/wellness-virivka-venkovni.jpg`,
    telephone: "+420608608101",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Kašava 302",
      postalCode: "763 19",
      addressLocality: "Kašava",
      addressCountry: "CZ",
    },
    amenityFeature: [
      { "@type": "LocationFeatureSpecification", name: "Venkovní vířivka", value: true },
      { "@type": "LocationFeatureSpecification", name: "Krytá vířivka až pro 5 osob", value: true },
      { "@type": "LocationFeatureSpecification", name: "Finská sauna vytápěná dřevem", value: true },
      { "@type": "LocationFeatureSpecification", name: "Prosklená odpočívárna", value: true },
    ],
  };

  /* Title, description, canonical a OG řeší globálně SeoManager
     (viz SEO_MAP → "/wellness"). Tady jen strukturovaná data navíc. */
  useJsonLd([serviceJsonLd, faqJsonLd], "wellness");

  return (
    <>
      {/* ================= NAVBAR – TOTÁLNÍ IZOLACE ================= */}
      <div className="relative z-50 isolate">
        <Navbar />
      </div>

      {/* ================= OBSAH ================= */}
      <main className="bg-[#F5E7CC] text-[#531d08] overflow-x-hidden">

        {/* =====================================================
            HERO
        ===================================================== */}
        <section className="relative isolate px-5 pt-14 pb-10 lg:pt-20 lg:pb-16 overflow-hidden">
          {/* pozadí – fotka wellness */}
          <img
            src="./img/wellness/wellness-uvodni.jpg"
            alt=""
            aria-hidden="true"
            loading="eager"
            className="absolute inset-0 -z-20 w-full h-full object-cover object-center"
          />
          {/* ztmavení, aby byl text čitelný na všech rozlišeních */}
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-[#2b1205]/70 sm:bg-[#2b1205]/65"
          />

          <div className="relative max-w-5xl mx-auto text-center text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.45)]">
            <p className="text-[11px] lg:text-xs uppercase tracking-[0.28em] text-[#f0d9b5]">
              Wellness v srdci Valašska
            </p>

            <h1 className="mt-4 text-3xl lg:text-5xl font-bold font-serif !leading-[1.35]">
              Wellness pod Rablinů
            </h1>

            <div className="w-12 h-px bg-[#e5c79b] mx-auto mt-6" />

            <p className="mt-6 text-sm lg:text-lg leading-relaxed max-w-3xl mx-auto">
              Venkovní odpočinkový
              prostor s výhledem do <b>valašských lesů a květinových luk</b>, dvě vířivky,
              finská sauna vytápěná dřevem a prosklená odpočívárna.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/rezervace-wellness" className="w-full sm:w-auto">
                <button className="w-full bg-white hover:bg-[#f3e3c8] transition-colors text-[#60260F] px-10 py-4 rounded-xl text-base lg:text-lg font-semibold">
                  Rezervovat wellness
                </button>
              </Link>
              <a href="tel:+420608608101" className="w-full sm:w-auto">
                <button className="w-full border border-white/80 text-white hover:bg-white hover:text-[#60260F] transition-colors px-10 py-4 rounded-xl text-base lg:text-lg font-semibold backdrop-blur-sm">
                  +420 608 608 101
                </button>
              </a>
            </div>
          </div>
        </section>

        {/* =====================================================
            BĚŽÍCÍ PÁS – WELLNESS V PŘÍPRAVĚ
        ===================================================== */}
        <section className="bg-[#60260F] text-[#f5e6c8] py-3 lg:py-4 overflow-hidden">
          <div className="flex w-max animate-marquee whitespace-nowrap motion-reduce:animate-none">
            {/* obsah dvakrát – druhá kopie navazuje bez skoku */}
            {[0, 1].map((kopie) => (
              <div key={kopie} className="flex shrink-0" aria-hidden={kopie === 1}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <span
                    key={i}
                    className="flex items-center text-xs lg:text-sm font-semibold uppercase tracking-[0.18em]"
                  >
                    Wellness je aktuálně v přípravě
                    <span className="mx-6 lg:mx-8 text-[#c9a878]">✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            PŘEDNOSTI – 4 IKONY
        ===================================================== */}
        <section className="px-5 lg:px-10 pt-12 lg:pt-20">
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {PREDNOSTI.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl border border-[#e8d6b8] shadow-[0_10px_30px_-18px_rgba(96,38,15,0.45)] p-6 text-center"
              >
                <span className="mx-auto mb-4 w-12 h-12 rounded-xl bg-[#f8efe1] text-[#60260F] flex items-center justify-center text-2xl">
                  {item.icon}
                </span>
                <h2 className="font-serif text-lg font-bold">{item.title}</h2>
                <p className="text-sm leading-relaxed mt-2 text-[#6b4530]">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* =====================================================
            VÍŘIVKY – 2 BLOKY
        ===================================================== */}
        <section className="px-5 lg:px-10 pt-16 lg:pt-24">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#8c5a3c]">Dvě vířivky</p>
            <h2 className="text-2xl lg:text-4xl font-bold font-serif !leading-[1.35] mt-3">
              Voda, která vypne hlavu
            </h2>
            <div className="w-12 h-px bg-[#c9a878] mx-auto mt-5" />
          </div>

          {/* VENKOVNÍ VÍŘIVKA */}
          <div className="max-w-6xl mx-auto mt-10 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            <img
              src="./img/wellness/wellness-virivka-venkovni.jpg"
              alt="Venkovní vířivka wellness pod noční oblohou"
              loading="lazy"
              className="w-full h-56 sm:h-80 lg:h-[380px] object-cover rounded-2xl shadow order-2 lg:order-1"
            />
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8c5a3c]">
                <GiWaterDrop /> Venkovní vířivka
              </span>
              <h3 className="text-xl lg:text-3xl font-serif font-bold mt-3">
                Pod noční oblohou
              </h3>
              <p className="text-sm lg:text-base leading-relaxed mt-4">
                Horká voda, chladný vzduch a nad vámi nebe plné hvězd – bez pouličního
                osvětlení, bez sousedů, bez ruchu. Vířivka je poháněná{" "}
                <b>novými masážními prvky</b>, které postupně rozpustí napětí z ramen,
                zad i nohou. Většina hostů z ní nechce ven ani po hodině.
              </p>
              <ul className="mt-5 space-y-2 text-sm lg:text-base">
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Kompletní sada moderních masážních trysek</li>
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Výhled na noční oblohu bez světelného smogu</li>
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Ideální po túře v Hostýnských vrších</li>
              </ul>
            </div>
          </div>

          {/* KRYTÁ VÍŘIVKA */}
          <div className="max-w-6xl mx-auto mt-12 lg:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            <div className="lg:order-2">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8c5a3c]">
                <FaHotTub /> Krytá vířivka
              </span>
              <h3 className="text-xl lg:text-3xl font-serif font-bold mt-3">
                Velká, krytá, až pro 5 osob
              </h3>
              <p className="text-sm lg:text-base leading-relaxed mt-4">
                Druhá vířivka je <b>více než dvakrát větší</b> a je zastřešená – takže
                funguje za deště, v mrazu i v poledním slunci. Pohodlně se do ní vejde{" "}
                <b>až 5 osob</b> a každý má své vlastní masážní místo. Žádné střídání,
                žádné mačkání: relaxuje celá parta najednou.
              </p>
              <p className="mt-4 text-sm lg:text-base leading-relaxed italic">
                <b>Teplotu vody nastavíme podle vás.</b> Stačí se předem domluvit
                s majitelem – ať už chcete horkou zimní lázeň, nebo svěžejší vodu
                na letní večer.
              </p>
            </div>
            <img
              src="./img/wellness/wellness-virivka-kryta.jpg"
              alt="Krytá vířivka wellness pro až 5 osob v Hospůdce pod Rablinů"
              loading="lazy"
              className="w-full h-56 sm:h-80 lg:h-[380px] object-cover rounded-2xl shadow lg:order-1"
            />
          </div>
        </section>

        {/* =====================================================
            SAUNA
        ===================================================== */}
        <section className="px-5 lg:px-10 pt-16 lg:pt-24">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <img
              src="./img/wellness/wellness-sauna-finska.jpg"
              alt="Prosklená finská sauna vytápěná saunovými kamny na dřevo ve wellness Hospůdky pod Rablinů"
              loading="lazy"
              className="w-full h-56 sm:h-80 lg:h-[400px] object-cover rounded-2xl shadow order-2 lg:order-1"
            />
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8c5a3c]">
                <GiHotSurface /> Finská sauna
              </span>
              <h2 className="text-2xl lg:text-4xl font-serif font-bold !leading-[1.35] mt-3">
                Sauna vytápěná saunovými kamny na dřevo
              </h2>
              <p className="text-sm lg:text-base leading-relaxed mt-4">
                Tohle není obyčejná elektrická kabina. Naše finská sauna je vytápěná{" "}
                <b>saunovými kamny na dřevo</b> – teplo je měkčí, sálavější a doprovází ho
                vůně dřeva a tichý praskot ohně. Rozdíl poznáte hned při první návštěvě;
                kdo ji jednou zkusí, elektrickou saunu už neřeší.
              </p>
              <ul className="mt-5 space-y-2 text-sm lg:text-base">
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Sálavé teplo ze saunových kamen na dřevo</li>
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Vůně dřeva a atmosféra pravé finské sauny</li>
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Ideální kombinace se studenou vodou a odpočívárnou</li>
              </ul>
            </div>
          </div>
        </section>

        {/* =====================================================
            ODPOČÍVÁRNA + PROSTOR
        ===================================================== */}
        <section className="px-5 lg:px-10 pt-16 lg:pt-24">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            <div className="lg:order-2">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8c5a3c]">
                <MdOutlineSpa /> Odpočívárna
              </span>
              <h2 className="text-2xl lg:text-4xl font-serif font-bold !leading-[1.35] mt-3">
                Prosklená odpočívárna s výhledem
              </h2>
              <p className="text-sm lg:text-base leading-relaxed mt-4">
                Mezi jednotlivými koly saunování se usadíte v <b>prosklené odpočívárně</b>,
                odkud se díváte přímo do lesů a na květinové louky. Uvnitř je ticho,
                venku se hýbou jen stromy – a čas se najednou zpomalí.
              </p>
              <p className="text-sm lg:text-base leading-relaxed mt-4">
                Celý prostor je vyskládaný z <b>krásné kamenné podlahy</b>, přírodního{" "}
                <b>dřeva</b>, moderního <b>kamene</b> a <b>živých rostlin</b>. Není to
                sterilní bazénová hala – je to místo, kde se chcete zdržet.
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl bg-white border border-[#e8d6b8] px-4 py-3 flex items-center gap-3">
                  <FaLeaf className="text-[#60260F]" />
                  <span className="text-sm">Rostliny, dřevo a kámen</span>
                </div>
                <div className="rounded-xl bg-white border border-[#e8d6b8] px-4 py-3 flex items-center gap-3">
                  <GiPineTree className="text-[#60260F]" />
                  <span className="text-sm">Výhled do lesů a luk</span>
                </div>
              </div>
            </div>

            <img
              src="./img/wellness/wellness-odpocivarna.jpg"
              alt="Prosklená odpočívárna wellness s lehátky na kamenné terase"
              loading="lazy"
              className="w-full h-56 sm:h-80 lg:h-[400px] object-cover rounded-2xl shadow lg:order-1"
            />
          </div>
        </section>


        {/* =====================================================
            GALERIE
        ===================================================== */}
        <section className="px-5 lg:px-10 pt-16 lg:pt-24">
          <div className="max-w-6xl mx-auto text-center mb-8">
            <h2 className="text-2xl lg:text-4xl font-serif font-bold !leading-[1.35]">Fotogalerie wellness</h2>
            <div className="w-12 h-px bg-[#c9a878] mx-auto mt-5" />
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {GALERIE.map((foto) => (
              <img
                key={foto.src}
                src={foto.src}
                alt={foto.alt}
                loading="lazy"
                className="w-full h-48 sm:h-56 lg:h-64 object-cover rounded-2xl shadow"
              />
            ))}
          </div>
        </section>

        {/* =====================================================
            CTA – ONLINE FORMULÁŘ REZERVACE WELLNESS
        ===================================================== */}
        <section className="px-5 lg:px-10 pt-16 lg:pt-24">
          <div className="max-w-5xl mx-auto rounded-3xl bg-white border border-[#e8d6b8] shadow-[0_20px_50px_-30px_rgba(96,38,15,0.5)] overflow-hidden grid grid-cols-1 lg:grid-cols-2">
            <img
              src="./img/wellness/wellness-sauna-finska.jpg"
              alt="Finská sauna a vířivky wellness Hospůdky pod Rablinů připravené k rezervaci"
              loading="lazy"
              className="w-full h-56 sm:h-72 lg:h-full object-cover"
            />
            <div className="p-7 lg:p-12 flex flex-col justify-center">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#8c5a3c]">
                Online poptávka
              </p>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold !leading-[1.35] mt-3">
                Zarezervujte si wellness za 2 minuty
              </h2>
              <p className="text-sm lg:text-base leading-relaxed mt-4 text-[#6b4530]">
                Vyplňte termín, počet osob a co si chcete užít – vířivky, saunu nebo obojí.
                Ozveme se vám zpět s potvrzením dostupnosti a domluvíme teplotu vody
                i čas rozehřátí sauny.
              </p>
              <ul className="mt-5 space-y-2 text-sm lg:text-base">
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Nezávazné a zdarma</li>
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Odpověď zpravidla do 24 hodin</li>
                <li className="flex gap-3"><span className="text-[#c9a878]">✦</span> Možnost připojit ubytování</li>
              </ul>
              <Link to="/rezervace-wellness" className="mt-8">
                <button className="w-full sm:w-auto bg-[#60260F] hover:bg-[#965126] transition-colors text-white px-10 py-4 rounded-xl text-base lg:text-lg font-semibold">
                  Vyplnit rezervační formulář
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* =====================================================
            FAQ
        ===================================================== */}
        <section className="px-5 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-24">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-4xl font-serif font-bold !leading-[1.35]">Časté dotazy</h2>
              <div className="w-12 h-px bg-[#c9a878] mx-auto mt-5" />
            </div>

            <div className="bg-white rounded-2xl border border-[#e0cbaa] shadow-[0_10px_30px_-15px_rgba(96,38,15,0.35)] overflow-hidden">
              {FAQ.map((item, i, arr) => (
                <details
                  key={item.q}
                  className={`group px-5 py-4 lg:px-7 lg:py-5 ${
                    i < arr.length - 1 ? "border-b border-[#efe3cf]" : ""
                  }`}
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-semibold text-[#3d2a1c]">
                    <span>{item.q}</span>
                    <span className="text-[#a07750] transition-transform duration-300 group-open:rotate-45 text-xl !leading-[1.35]">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-sm lg:text-base leading-relaxed text-[#6b4530]">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>

            {/* ZÁVĚREČNÉ CTA */}
            <div className="mt-10 text-center">
              <p className="text-sm lg:text-base leading-relaxed max-w-2xl mx-auto">
                Kapacita wellness je omezená – vždy ho pronajímáme jen jedné skupině.
                Oblíbené termíny (pátky, soboty a svátky) mizí nejrychleji.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/rezervace-wellness" className="w-full sm:w-auto">
                  <button className="w-full bg-[#60260F] hover:bg-[#965126] transition-colors text-white px-10 py-4 rounded-xl text-base lg:text-lg font-semibold">
                    Rezervovat wellness
                  </button>
                </Link>
                <Link to="/ubytovani" className="w-full sm:w-auto">
                  <button className="w-full border border-[#60260F] text-[#60260F] hover:bg-[#60260F] hover:text-white transition-colors px-10 py-4 rounded-xl text-base lg:text-lg font-semibold">
                    Spojit s ubytováním
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default Wellness;
